// pages/admin/ItinerarioAdmin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bus, Calendar, Clock, MapPin, DollarSign, 
  Users, CheckCircle, XCircle, Clock as ClockIcon,
  Eye, X, RefreshCw, Filter, User, UserCheck, GitBranch,
  Building2, Mail
} from 'lucide-react';
import AdminLayout from '../../layouts/AdminLayout';
import Modal from '../../components/Modal';
import ModalConfirm from '../../components/ModalConfirm';
import { showSuccess, showError, showLoading, dismissToast } from '../../utils/toast';

const API_URL = process.env.REACT_APP_API_URL;

const ItinerarioAdmin = () => {
  const navigate = useNavigate();
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFecha, setSelectedFecha] = useState(new Date().toISOString().split('T')[0]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [reservas, setReservas] = useState([]);
  const [selectedServicioId, setSelectedServicioId] = useState(null);
  
  // ✅ métodos de pago con cuentas
  const [metodosPago, setMetodosPago] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  
  const [showReservaDetailModal, setShowReservaDetailModal] = useState(false);
  const [selectedReservaDetalle, setSelectedReservaDetalle] = useState(null);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirmar',
    confirmButtonColor: '#e83e8c'
  });

  useEffect(() => {
    cargarServicios();
    cargarEstadisticas();
    cargarTodasLasReservas();
    cargarMetodosPago();
  }, []);

  const cargarServicios = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/servicios`);
      if (!response.ok) throw new Error('Error al cargar servicios');
      const data = await response.json();
      setServicios(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/estadisticas`);
      if (!response.ok) throw new Error('Error al cargar estadísticas');
      const data = await response.json();
      setEstadisticas(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const cargarTodasLasReservas = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/reservas`);
      if (!response.ok) throw new Error('Error al cargar reservas');
      const data = await response.json();
      setReservas(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // ✅ cargar métodos de pago y aplanar cuentas
  const cargarMetodosPago = async () => {
    try {
      const response = await fetch(`${API_URL}/metodospago`);
      if (!response.ok) throw new Error('Error al cargar métodos de pago');
      const data = await response.json();
      setMetodosPago(data);
      
      const todasLasCuentas = [];
      data.forEach(metodo => {
        if (metodo.cuentas && metodo.cuentas.length > 0) {
          metodo.cuentas.forEach(cuenta => {
            todasLasCuentas.push({
              ...cuenta,
              metodoPagoId: metodo.id,
              metodoPagoNombre: metodo.nombre
            });
          });
        }
      });
      setCuentasBancarias(todasLasCuentas);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getOrigenDestinoReserva = (reserva) => {
    if (!reserva) return { origen: '', destino: '', esTramo: false, tramos: [] };
    
    if (reserva.tramoIds && reserva.servicio?.tramos) {
      try {
        const tramoIdsArray = reserva.tramoIds.split(',').map(Number);
        const tramosSeleccionados = reserva.servicio.tramos
          .filter(t => tramoIdsArray.includes(t.id))
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        
        if (tramosSeleccionados.length > 0) {
          return {
            origen: tramosSeleccionados[0].origen,
            destino: tramosSeleccionados[tramosSeleccionados.length - 1].destino,
            esTramo: true,
            tramos: tramosSeleccionados,
            precioTotal: tramosSeleccionados.reduce((sum, t) => sum + t.precio, 0)
          };
        }
      } catch (e) {
        console.error('Error al parsear TramoIds:', e);
      }
    }
    
    return {
      origen: reserva.servicio?.origen || '',
      destino: reserva.servicio?.destino || '',
      esTramo: false,
      tramos: [],
      precioTotal: reserva.total || 0
    };
  };

  // ✅ obtener la cuenta bancaria asociada a una reserva
  const getCuentaDeReserva = (reserva) => {
    if (!reserva?.metodoPago) return null;
    
    const metodo = metodosPago.find(m => 
      m.nombre.toLowerCase() === reserva.metodoPago.toLowerCase()
    );
    
    if (!metodo || !metodo.cuentas || metodo.cuentas.length === 0) return null;
    
    return metodo.cuentas[0];
  };

  // ✅ obtener solo el nombre del banco
  const getNombreBanco = (reserva) => {
    const cuenta = getCuentaDeReserva(reserva);
    return cuenta?.nombreBanco || null;
  };

  const getReservasPorServicio = (servicioId) => {
    return reservas.filter(r => r.servicioId === servicioId && r.estado !== 'Cancelado');
  };

  const getEstadisticas = () => {
    if (selectedServicioId) {
      const todasLasReservasDelServicio = reservas.filter(r => r.servicioId === selectedServicioId);
      
      const pendientes = todasLasReservasDelServicio.filter(r => r.estado === 'Pendiente').length;
      const pagados = todasLasReservasDelServicio.filter(r => r.estado === 'Pagado').length;
      const cancelados = todasLasReservasDelServicio.filter(r => r.estado === 'Cancelado').length;
      
      return { pendientes, pagados, cancelados, esFiltrado: true };
    }

    const serviciosDelDia = servicios.filter(s => {
      if (!s.fecha) return false;
      return new Date(s.fecha).toISOString().split('T')[0] === selectedFecha;
    });

    const idsServiciosDelDia = serviciosDelDia.map(s => s.id);
    
    const todasLasReservasDelDia = reservas.filter(r => 
      idsServiciosDelDia.includes(r.servicioId)
    );

    const pendientes = todasLasReservasDelDia.filter(r => r.estado === 'Pendiente').length;
    const pagados = todasLasReservasDelDia.filter(r => r.estado === 'Pagado').length;
    const cancelados = todasLasReservasDelDia.filter(r => r.estado === 'Cancelado').length;
    
    return { pendientes, pagados, cancelados, esFiltrado: false };
  };

  const handleConfirmarPago = async (reserva) => {
    setConfirmConfig({
      title: 'Confirmar pago',
      message: `¿Confirmar pago de la reserva <strong>${reserva.codigoUnico}</strong>?<br/><br/>Total: <strong>$${formatPrecio(reserva.total)}</strong>${reserva.metodoPago === 'Transferencia' ? `<br/>Comprobante: ${reserva.comprobante}` : ''}`,
      onConfirm: async () => {
        const toastId = showLoading('Confirmando pago...');
        
        try {
          const response = await fetch(`${API_URL}/admin/reservas/${reserva.id}/confirmar-pago`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!response.ok) throw new Error('Error al confirmar pago');
          
          dismissToast(toastId);
          showSuccess('La reserva ha sido marcada como pagada');
          await cargarTodasLasReservas();
          await cargarServicios();
          setShowDetailModal(false);
          setShowReservaDetailModal(false);
        } catch (error) {
          console.error('Error:', error);
          dismissToast(toastId);
          showError('No se pudo confirmar el pago');
        }
        setShowConfirmModal(false);
      },
      confirmText: 'Sí, confirmar pago',
      confirmButtonColor: '#22c55e'
    });
    setShowConfirmModal(true);
  };

  const handleCancelarReserva = async (reserva) => {
    setConfirmConfig({
      title: 'Cancelar reserva',
      message: `¿Estás seguro de cancelar la reserva <strong>${reserva.codigoUnico}</strong>?<br/><span style="color:#dc2626;font-size:12px;">Esta acción no se puede deshacer.</span>`,
      onConfirm: async () => {
        const toastId = showLoading('Cancelando reserva...');
        
        try {
          const response = await fetch(`${API_URL}/admin/reservas/${reserva.id}/cancelar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!response.ok) throw new Error('Error al cancelar reserva');
          
          dismissToast(toastId);
          showSuccess('La reserva ha sido cancelada correctamente');
          await cargarTodasLasReservas();
          await cargarServicios();
          setShowDetailModal(false);
          setShowReservaDetailModal(false);
        } catch (error) {
          console.error('Error:', error);
          dismissToast(toastId);
          showError('No se pudo cancelar la reserva');
        }
        setShowConfirmModal(false);
      },
      confirmText: 'Sí, cancelar reserva',
      confirmButtonColor: '#dc2626'
    });
    setShowConfirmModal(true);
  };

  const handleVerDetalles = (servicio) => {
    setSelectedServicio(servicio);
    setSelectedServicioId(servicio.id);
    setShowDetailModal(true);
  };

  const verDetallesReserva = (reserva) => {
    setSelectedReservaDetalle(reserva);
    setShowReservaDetailModal(true);
  };

  const getPasajerosAdicionales = (nombresCompletos) => {
    if (!nombresCompletos) return [];
    if (nombresCompletos === '') return [];
    
    if (typeof nombresCompletos === 'string' && !nombresCompletos.includes(',')) {
      return [];
    }
    
    if (typeof nombresCompletos === 'string') {
      const lista = nombresCompletos.split(',').map(n => n.trim()).filter(n => n !== '');
      if (lista.length > 1) {
        return lista.slice(1);
      }
      return [];
    }
    
    if (Array.isArray(nombresCompletos)) {
      if (nombresCompletos.length > 1) {
        return nombresCompletos.slice(1);
      }
      return [];
    }
    
    return [];
  };

  const getCedulasAdicionales = (cedulasCompletas) => {
    if (!cedulasCompletas) return [];
    if (cedulasCompletas === '') return [];
    
    if (typeof cedulasCompletas === 'string' && !cedulasCompletas.includes(',')) {
      return [cedulasCompletas.trim()];
    }
    
    if (typeof cedulasCompletas === 'string') {
      const lista = cedulasCompletas.split(',').map(c => c.trim()).filter(c => c !== '');
      return lista;
    }
    
    if (Array.isArray(cedulasCompletas)) {
      return cedulasCompletas;
    }
    
    return [];
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    const date = new Date(fecha);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const newDate = new Date(Date.UTC(year, month, day));
    return newDate.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'Pagado': return 'bg-green-100 text-green-800';
      case 'Cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'Pendiente': return <ClockIcon className="w-4 h-4" />;
      case 'Pagado': return <CheckCircle className="w-4 h-4" />;
      case 'Cancelado': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getMetodoPagoNombre = (metodoPago) => {
    if (!metodoPago) return 'Efectivo';
    if (metodoPago.toLowerCase() === 'transferencia') return 'Transferencia';
    return metodoPago;
  };

  const serviciosFiltrados = servicios.filter(s => {
    if (!s.fecha) return false;
    const fechaServicio = new Date(s.fecha).toISOString().split('T')[0];
    return fechaServicio === selectedFecha;
  });

  const serviciosPorHorario = [...serviciosFiltrados].sort((a, b) => 
    (a.horaSalida || '').localeCompare(b.horaSalida || '')
  );

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const currentDate = new Date(selectedFecha + 'T00:00:00');
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const handleDateClick = (day) => {
    const newDate = new Date(Date.UTC(year, month, day));
    const yearStr = newDate.getUTCFullYear();
    const monthStr = String(newDate.getUTCMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    setSelectedFecha(`${yearStr}-${monthStr}-${dayStr}`);
    setSelectedServicioId(null);
    setSelectedServicio(null);
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('es-AR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  };

  const stats = getEstadisticas();

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Itinerario de Viajes</h1>
          <p className="text-gray-500 text-sm">Visualizá todos los servicios y sus reservas</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reservas pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendientes || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <ClockIcon className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reservas pagadas</p>
                <p className="text-2xl font-bold text-[#e83e8c]">{stats.pagados || 0}</p>
              </div>
              <div className="p-3 bg-pink-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-[#e83e8c]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reservas canceladas</p>
                <p className="text-2xl font-bold text-red-600">{stats.cancelados || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 capitalize">
                {getMonthName(currentDate)}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newDate = new Date(Date.UTC(year, month - 1, 1));
                    const yearStr = newDate.getUTCFullYear();
                    const monthStr = String(newDate.getUTCMonth() + 1).padStart(2, '0');
                    setSelectedFecha(`${yearStr}-${monthStr}-01`);
                    setSelectedServicioId(null);
                    setSelectedServicio(null);
                  }}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  ←
                </button>
                <button
                  onClick={() => {
                    const newDate = new Date(Date.UTC(year, month + 1, 1));
                    const yearStr = newDate.getUTCFullYear();
                    const monthStr = String(newDate.getUTCMonth() + 1).padStart(2, '0');
                    setSelectedFecha(`${yearStr}-${monthStr}-01`);
                    setSelectedServicioId(null);
                    setSelectedServicio(null);
                  }}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  →
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
              {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'].map(day => (
                <div key={day} className="py-1">{day}</div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                <div key={`empty-${i}`} className="h-10" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const tieneServicios = servicios.some(s => {
                  if (!s.fecha) return false;
                  return s.fecha.split('T')[0] === dateStr;
                });
                const esHoy = dateStr === new Date().toISOString().split('T')[0];
                const esSeleccionada = dateStr === selectedFecha;
                
                return (
                  <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={`
                      h-10 rounded-lg flex items-center justify-center text-sm transition-all
                      ${esSeleccionada ? 'bg-[#e83e8c] text-white' : 'hover:bg-gray-100'}
                      ${tieneServicios && !esSeleccionada ? 'bg-pink-50 text-pink-700 font-medium' : ''}
                      ${esHoy && !esSeleccionada ? 'border-2 border-[#e83e8c]' : ''}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-gray-700">
                    {formatFecha(selectedFecha)}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">
                    {serviciosPorHorario.length} servicio(s)
                  </span>
                </div>
              </div>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e83e8c] mx-auto"></div>
                  <p className="text-gray-500 mt-3">Cargando servicios...</p>
                </div>
              ) : serviciosPorHorario.length === 0 ? (
                <div className="text-center py-12">
                  <Bus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay servicios programados para esta fecha</p>
                  <button
                    onClick={() => navigate('/admin/servicios')}
                    className="mt-3 text-sm text-[#e83e8c] underline hover:text-[#c2185b]"
                  >
                    Crear nuevo servicio
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {serviciosPorHorario.map((servicio) => {
                    const reservasDelServicio = getReservasPorServicio(servicio.id);
                    const totalPasajeros = reservasDelServicio.reduce((sum, r) => sum + (r.cantidadPasajeros || 1), 0);
                    const porcentaje = Math.min(totalPasajeros * 5, 100);
                    const esSeleccionado = selectedServicioId === servicio.id;
                    
                    return (
                      <div 
                        key={servicio.id} 
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${esSeleccionado ? 'bg-pink-50 border-l-4 border-[#e83e8c]' : ''}`}
                        onClick={() => {
                          if (selectedServicioId === servicio.id) {
                            setSelectedServicioId(null);
                            setSelectedServicio(null);
                          } else {
                            setSelectedServicioId(servicio.id);
                            setSelectedServicio(servicio);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">{servicio.horaSalida?.substring(0,5)}</span>
                                <span className="text-gray-400">-</span>
                                <span>{servicio.horaLlegada?.substring(0,5)}</span>
                              </div>
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span>{servicio.origen} → {servicio.destino}</span>
                              </div>
                            </div>
                            
                            <div className="mt-2">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Pasajeros confirmados</span>
                                <span className="font-medium text-[#e83e8c]">
                                  {totalPasajeros} pasajeros
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full transition-all bg-[#e83e8c]"
                                  style={{ width: `${porcentaje}%` }}
                                />
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                {reservasDelServicio.length} reservas
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerDetalles(servicio);
                              }}
                              className="text-xs bg-[#e83e8c] text-white px-3 py-1 rounded-lg hover:bg-[#c2185b] flex items-center gap-1 mx-auto"
                            >
                              <Eye className="w-3 h-3" />
                              Ver detalles
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalles del servicio */}
      {showDetailModal && selectedServicio && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full my-8">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Detalles del servicio</h3>
                <p className="text-sm text-gray-500">{selectedServicio.origen} → {selectedServicio.destino}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-xl p-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Fecha</p>
                  <p className="font-medium text-gray-800">{formatFecha(selectedServicio.fecha)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Horario</p>
                  <p className="font-medium text-gray-800">{selectedServicio.horaSalida?.substring(0,5)} - {selectedServicio.horaLlegada?.substring(0,5)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pasajeros</p>
                  <p className="font-medium text-gray-800">{getReservasPorServicio(selectedServicio.id).length}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-[#e83e8c]" />
                  Pasajeros ({getReservasPorServicio(selectedServicio.id).length})
                </h4>
                
                {(() => {
                  const reservasDelServicio = getReservasPorServicio(selectedServicio.id);
                  
                  if (reservasDelServicio.length === 0) {
                    return (
                      <div className="text-center py-8 bg-gray-50 rounded-xl">
                        <p className="text-gray-500 text-sm">No hay reservas para este servicio</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-2">
                      {reservasDelServicio.map((reserva) => {
                        const infoTramo = getOrigenDestinoReserva(reserva);
                        const metodoPagoDisplay = getMetodoPagoNombre(reserva.metodoPago);
                        const nombreBanco = getNombreBanco(reserva);
                        
                        return (
                          <div key={reserva.id} className="border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(reserva.estado)}`}>
                                  {getEstadoIcon(reserva.estado)}
                                  {reserva.estado}
                                </span>
                                <span className="font-medium text-gray-800 text-sm">{reserva.nombrePasajero}</span>
                                <span className="text-xs text-gray-500">{reserva.telefonoPasajero}</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-bold text-gray-700">
                                  ${formatPrecio(reserva.total)}
                                </span>
                                <button
                                  onClick={() => verDetallesReserva(reserva)}
                                  className="p-1 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Ver detalles completos"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {reserva.estado === 'Pendiente' && (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleConfirmarPago(reserva)}
                                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                                      title="Confirmar pago"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleCancelarReserva(reserva)}
                                      className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Cancelar reserva"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="mt-2 text-xs">
                              {infoTramo.esTramo ? (
                                <div className="flex items-center gap-2 text-blue-600">
                                  <GitBranch className="w-3 h-3" />
                                  <span className="font-medium">Tramo comprado:</span>
                                  <span>{infoTramo.origen} → {infoTramo.destino}</span>
                                  <span className="text-gray-400">|</span>
                                  <span className="font-bold text-gray-700">${formatPrecio(infoTramo.precioTotal)}</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-gray-500">
                                  <Bus className="w-3 h-3" />
                                  <span>Servicio completo: {selectedServicio.origen} → {selectedServicio.destino}</span>
                                  <span className="ml-1 text-[#e83e8c] font-bold">${formatPrecio(reserva.total)}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* ✅ Pago + banco al lado */}
                            <div className="mt-1 grid grid-cols-3 gap-2 text-xs text-gray-500">
                              <span><strong>Ticket:</strong> {reserva.codigoUnico}</span>
                              <span><strong>Pasajeros:</strong> {reserva.cantidadPasajeros || 1}</span>
                              <span className="flex items-center gap-1 flex-wrap">
                                <strong>Pago:</strong> {metodoPagoDisplay}
                                {metodoPagoDisplay === 'Transferencia' && reserva.comprobante && (
                                  <span className="text-blue-600 font-medium">
                                    N° {reserva.comprobante}
                                  </span>
                                )}
                                {nombreBanco && (
                                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-100 rounded px-1.5 py-0.5 font-medium">
                                    <Building2 className="w-3 h-3" />
                                    {nombreBanco}
                                  </span>
                                )}
                              </span>
                            </div>

                            {reserva.nombresPasajeros && reserva.cantidadPasajeros > 1 && (
                              <p className="text-xs text-gray-400 mt-1">
                                <strong>Pasajeros:</strong> {reserva.nombresPasajeros}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="p-5 border-t border-gray-100">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles de Reserva */}
      {showReservaDetailModal && selectedReservaDetalle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-8">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">Detalles de la reserva</h3>
              <button onClick={() => setShowReservaDetailModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-[#e83e8c]/10 rounded-xl p-4 text-center border border-[#e83e8c]/20">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Número de ticket</p>
                <p className="text-2xl font-bold font-mono text-[#e83e8c]">{selectedReservaDetalle.codigoUnico}</p>
              </div>

              <div className="flex justify-center">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(selectedReservaDetalle.estado)}`}>
                  {getEstadoIcon(selectedReservaDetalle.estado)}
                  {selectedReservaDetalle.estado}
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Bus className="w-4 h-4 text-[#e83e8c]" />
                  Datos del viaje
                </h4>
                <div className="space-y-2 text-sm">
                  {(() => {
                    const infoTramo = getOrigenDestinoReserva(selectedReservaDetalle);
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Origen - Destino:</span>
                          <span className="font-medium">
                            {infoTramo.esTramo ? (
                              <span className="text-blue-600">
                                {infoTramo.origen} → {infoTramo.destino}
                                <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Tramo</span>
                              </span>
                            ) : (
                              `${infoTramo.origen} → ${infoTramo.destino}`
                            )}
                          </span>
                        </div>
                        {infoTramo.esTramo && infoTramo.tramos.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tramos comprados:</span>
                            <span className="font-medium text-xs">
                              {infoTramo.tramos.map((t, i) => (
                                <span key={i}>
                                  {i > 0 && ' → '}
                                  {t.origen} → {t.destino}
                                </span>
                              ))}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fecha:</span>
                          <span className="font-medium">{formatFecha(selectedReservaDetalle.servicio?.fecha)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Horario:</span>
                          <span className="font-medium">Sale: {selectedReservaDetalle.servicio?.horaSalida?.substring(0,5)} - Llega: {selectedReservaDetalle.servicio?.horaLlegada?.substring(0,5)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                <h4 className="font-semibold text-[#e83e8c] mb-3 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Datos del titular
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nombre:</span>
                    <span className="font-medium text-gray-800">{selectedReservaDetalle.nombrePasajero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cédula:</span>
                    <span className="font-medium text-gray-800">{selectedReservaDetalle.cedulaTitular || 'No registrada'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Teléfono WhatsApp:</span>
                    <span className="font-medium text-gray-800">{selectedReservaDetalle.telefonoPasajero}</span>
                  </div>
                  {selectedReservaDetalle.telefonoParticular && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Teléfono particular:</span>
                      <span className="font-medium text-gray-800">{selectedReservaDetalle.telefonoParticular}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedReservaDetalle.cantidadPasajeros > 1 && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#e83e8c]" />
                    Pasajeros adicionales ({selectedReservaDetalle.cantidadPasajeros - 1})
                  </h4>
                  {(() => {
                    const nombresAdicionales = getPasajerosAdicionales(selectedReservaDetalle.nombresPasajeros);
                    const cedulasAdicionales = getCedulasAdicionales(selectedReservaDetalle.cedulasPasajeros);
                    
                    if (nombresAdicionales.length === 0) {
                      return <p className="text-sm text-gray-500">No hay pasajeros adicionales registrados</p>;
                    }
                    
                    return (
                      <ul className="space-y-2">
                        {nombresAdicionales.map((nombre, index) => (
                          <li key={index} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{nombre}</span>
                            </div>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Cédula: {cedulasAdicionales[index] || 'No registrada'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Información de pago
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Método de pago:</span>
                    <span className="font-medium flex items-center gap-2">
                      {getMetodoPagoNombre(selectedReservaDetalle.metodoPago)}
                      {/* ✅ Nombre del banco al lado del método de pago */}
                      {(() => {
                        const nombreBanco = getNombreBanco(selectedReservaDetalle);
                        if (!nombreBanco) return null;
                        return (
                          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-100 rounded px-1.5 py-0.5 text-xs font-medium">
                            <Building2 className="w-3 h-3" />
                            {nombreBanco}
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                  {selectedReservaDetalle.metodoPago === 'Transferencia' && selectedReservaDetalle.comprobante && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° de comprobante:</span>
                      <span className="font-medium font-mono">{selectedReservaDetalle.comprobante}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#1a1a2e] text-white rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold text-[#e83e8c]">${formatPrecio(selectedReservaDetalle.total)}</span>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowReservaDetailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
              {selectedReservaDetalle.estado === 'Pendiente' && (
                <>
                  <button
                    onClick={() => {
                      setShowReservaDetailModal(false);
                      handleConfirmarPago(selectedReservaDetalle);
                    }}
                    className="flex-1 px-4 py-2 bg-[#e83e8c] hover:bg-[#c2185b] text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirmar pago
                  </button>
                  <button
                    onClick={() => {
                      setShowReservaDetailModal(false);
                      handleCancelarReserva(selectedReservaDetalle);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ModalConfirm
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        confirmButtonColor={confirmConfig.confirmButtonColor}
      />
    </AdminLayout>
  );
};

export default ItinerarioAdmin;